import { useState } from 'react';
import { Info } from 'lucide-react';
import {
  useAddAllowedEndpoints,
  useAllowedEndpoints,
  usePromoteCurrentEndpoints,
  useRemoveAllowedEndpoint,
} from '@/hooks/useSecurityPolicy';
import { useEndpointsForInterface } from '@/hooks/useEndpoints';
import { AppSheet } from '@/components/ui/app-sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface Props {
  interfaceId: string;
  interfaceName: string;
  onClose: () => void;
}

/**
 * InterfaceAllowlistModal — manage the per-port endpoint allowlist
 * for ports in `strict` security mode. Used by DevicePortsTab.
 *
 * Two sections:
 *   • Currently allowed (with Remove buttons)
 *   • Currently visible but not yet allowed (with Allow buttons
 *     and a bulk-promote action)
 *
 * Wider AppSheet (lg) since both tables are 4 columns and need room.
 */
export default function InterfaceAllowlistModal({
  interfaceId,
  interfaceName,
  onClose,
}: Props) {
  const { data: allowed, isLoading: allowedLoading } = useAllowedEndpoints(interfaceId);
  const { data: currentlyVisible, isLoading: currentLoading } =
    useEndpointsForInterface(interfaceId);
  const promoteMutation = usePromoteCurrentEndpoints(interfaceId);
  const addMutation = useAddAllowedEndpoints(interfaceId);
  const removeMutation = useRemoveAllowedEndpoint(interfaceId);
  const [feedback, setFeedback] = useState<string | null>(null);

  const allowedSet = new Set(allowed?.map((a) => a.endpointId) ?? []);
  const unallowedCurrent =
    currentlyVisible?.endpoints.filter((ep) => !allowedSet.has(ep.id)) ?? [];

  const handlePromote = () => {
    promoteMutation.mutate(undefined, {
      onSuccess: (r) => {
        setFeedback(
          `Added ${String(r.added)} endpoint(s) (${String(r.alreadyPresent)} already present).`,
        );
      },
    });
  };

  const handleAddOne = (endpointId: string) => {
    addMutation.mutate([endpointId], {
      onSuccess: (r) => {
        setFeedback(`Added ${String(r.added)} endpoint.`);
      },
    });
  };

  const handleRemove = (endpointId: string) => {
    removeMutation.mutate(endpointId, {
      onSuccess: () => {
        setFeedback('Removed from allowlist.');
      },
    });
  };

  return (
    <AppSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Allowlist for ${interfaceName}`}
      description="In strict mode, only the MAC addresses in this list are tolerated on this port. Anything else triggers a 'Rogue device on strict port' incident."
      size="lg"
    >
      <AppSheet.Body>
        {feedback ? (
          <Alert className="mb-4">
            <Info className="h-4 w-4" />
            <AlertDescription>{feedback}</AlertDescription>
          </Alert>
        ) : null}

        <section className="mb-6">
          <h4 className="text-sm font-semibold mb-3">Currently allowed</h4>
          {allowedLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (allowed?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              No endpoints on the allowlist. Add some below.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground uppercase tracking-wider">
                <tr className="border-b border-border-subtle">
                  <th className="py-2 text-left font-medium">MAC</th>
                  <th className="py-2 text-left font-medium hidden md:table-cell">Vendor</th>
                  <th className="py-2 text-left font-medium hidden md:table-cell">Hostname</th>
                  <th className="py-2 text-left font-medium hidden md:table-cell">Added</th>
                  <th className="py-2 text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {allowed?.map((a) => (
                  <tr key={a.endpointId}>
                    <td className="py-2">
                      <code className="font-mono text-xs">{a.macAddress}</code>
                    </td>
                    <td className="py-2 hidden md:table-cell">
                      {a.ouiVendor ?? <span className="text-muted-foreground">Unknown</span>}
                    </td>
                    <td className="py-2 hidden md:table-cell">{a.hostname ?? '—'}</td>
                    <td className="py-2 hidden md:table-cell text-xs text-muted-foreground">
                      {new Date(a.addedAt).toLocaleDateString()}
                    </td>
                    <td className="py-2 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { handleRemove(a.endpointId); }}
                        className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section>
          <h4 className="text-sm font-semibold mb-3">Currently visible (not yet on the allowlist)</h4>
          {currentLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : unallowedCurrent.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No additional endpoints visible right now.
            </p>
          ) : (
            <>
              <div className="mb-3">
                <Button
                  size="sm"
                  onClick={handlePromote}
                  disabled={promoteMutation.isPending}
                >
                  {promoteMutation.isPending
                    ? 'Adding…'
                    : `Allow all ${String(unallowedCurrent.length)} current endpoint(s)`}
                </Button>
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground uppercase tracking-wider">
                  <tr className="border-b border-border-subtle">
                    <th className="py-2 text-left font-medium">MAC</th>
                    <th className="py-2 text-left font-medium hidden md:table-cell">Vendor</th>
                    <th className="py-2 text-left font-medium hidden md:table-cell">Hostname</th>
                    <th className="py-2 text-right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {unallowedCurrent.map((ep) => (
                    <tr key={ep.id}>
                      <td className="py-2">
                        <code className="font-mono text-xs">{ep.macAddress}</code>
                      </td>
                      <td className="py-2 hidden md:table-cell">
                        {ep.ouiVendor ?? <span className="text-muted-foreground">Unknown</span>}
                      </td>
                      <td className="py-2 hidden md:table-cell">{ep.hostname ?? '—'}</td>
                      <td className="py-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { handleAddOne(ep.id); }}
                        >
                          Allow
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>
      </AppSheet.Body>
      <AppSheet.Footer>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </AppSheet.Footer>
    </AppSheet>
  );
}
